import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import type { SearchPoliceReportOutput } from '@/types/browserAutomation';

import { LogsTable } from './LogsTable';
import { SearchResultsTable } from './SearchResultsTable';

interface SearchResultsSectionProps {
  searchOutput: SearchPoliceReportOutput;
}

const SearchStatusBadge = ({ status }: { status: string }) => {
  const config = {
    found_one: {
      icon: <CheckCircleIcon className="h-4 w-4" />,
      label: 'Found One',
      className: 'bg-green-100 text-green-700',
    },
    found_multiple: {
      icon: <ExclamationTriangleIcon className="h-4 w-4" />,
      label: 'Found Multiple',
      className: 'bg-yellow-100 text-yellow-700',
    },
    not_found: {
      icon: <XCircleIcon className="h-4 w-4" />,
      label: 'Not Found',
      className: 'bg-gray-100 text-gray-600',
    },
    failed: {
      icon: <XCircleIcon className="h-4 w-4" />,
      label: 'Failed',
      className: 'bg-red-100 text-red-700',
    },
  };

  const { icon, label, className } =
    config[status as keyof typeof config] || config.failed;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${className}`}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
};

export const SearchResultsSection = ({
  searchOutput,
}: SearchResultsSectionProps) => {
  const searchData = searchOutput.data;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-base font-semibold text-gray-900">
            Search Phase
          </h3>
        </div>
        <SearchStatusBadge status={searchData.status} />
      </div>

      {/* Details/Error */}
      {searchData.details && (
        <p className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded p-2">
          {searchData.details}
        </p>
      )}
      {searchData.error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <div className="font-medium mb-1">Error</div>
          <div>{searchData.error}</div>
        </div>
      )}

      {/* Found records table */}
      {searchData.records && searchData.records.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            {searchData.records.length} record
            {searchData.records.length !== 1 ? 's' : ''}
          </div>
          <SearchResultsTable records={searchData.records} />
        </div>
      )}

      {/* Logs */}
      {searchOutput.logs && searchOutput.logs.length > 0 && (
        <details>
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            Search Logs ({searchOutput.logs.length})
          </summary>
          <div className="mt-3">
            <LogsTable logs={searchOutput.logs} />
          </div>
        </details>
      )}
    </div>
  );
};
